import Vue from "vue";
import VueRouter from "vue-router";
import VueMeta from "vue-meta";
import routes from "./routes";
import store from "@/state/store";

Vue.use(VueRouter);
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: "page",
});

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (
      to.name === from.name &&
      to.matched.some((routeDef) => routeDef.meta.preventScrollIfItIsSameRoute)
    ) {
      return false;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.onError((error) => {
  if (error.name === "ChunkLoadError" && window.location.hash !== "#retry") {
    // the chunk might no longer be available due to a recent redeployment of the page
    // mark the page to don't trigger reload infinitely
    window.location.hash = "#retry";
    window.location.reload(true);
  }
});

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  // Check if auth is required on this route
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  // If auth isn't required for the route, just continue.
  if (!authRequired) {
    return next();
  }

  // If auth is required and the user is logged in...
  if (store.getters["auth/loggedIn"]) {
    // Load user data if it is not loaded
    if (!store.getters["user/hasUser"]) {
      await store.dispatch("user/getUserData");
    }

    // // If a user tries to access to a route that does not belong to him
    // if (routeTo.meta.userType && routeTo.meta.userType !== store.getters['user/userType']) {
    //     return next({name: store.getters['user/homeRouteName'], query: {redirectFrom: routeTo.fullPath}})
    // }

    // City-Express-only accounts (e.g. logistika@bigznet.rs) may only reach
    // the City Express screen; send every other route back to it.
    if (
      store.getters["user/isCityExpressOnly"] &&
      routeTo.name !== "super_admin_city_express_v2"
    ) {
      return next({ name: "super_admin_city_express_v2" });
    }

    return next();
  } else {
    next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
  }
});

router.afterEach(async (routeTo) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  if (!authRequired) {
    store.dispatch("user/clearState");
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

export default router;
